import { useCallback, useRef } from "react";
import { downloadVideo, handleDownloadError } from "@src/features/clips";
import type { Clip } from "@src/features/clips";
import type { CompilationListItem } from "@visioni/a3s-media/app";

export interface UseCompilationWatchResult {
  /** `null` until a ready item with a signed video URL is being watched. */
  readonly clip: Clip | null;
  /** `undefined` when there's nothing to download yet (e.g. a not-yet-redeployed Lambda). */
  readonly handleDownload: (() => void) | undefined;
}

/**
 * Shapes a `CompilationListItem` into what the app-specific `ClipVideoPlayer`/
 * download flow need — the surrounding layout is `CompilationWatchView` from
 * `@visioni/a3s-media/app`, which knows nothing about `expo-video` or this
 * app's download utility.
 */
export function useCompilationWatch(item: CompilationListItem | null): UseCompilationWatchResult {
  const handleDownload = useCallback(() => {
    if (!item?.downloadUrl) return;
    // `attachment` because `downloadUrl` is signed with a filename and so
    // carries `Content-Disposition: attachment` — the browser saves the file
    // rather than opening it, which is what the toast then reports.
    void downloadVideo({ videoUrl: item.downloadUrl, timestamp: new Date(), attachment: true }).catch(
      handleDownloadError,
    );
  }, [item]);

  // The URL is re-signed on every list read — polling, window focus, remount —
  // so it is a different string each time even though it addresses the same
  // object. Feeding that straight to the player would swap the `<video>` src
  // mid-playback and restart the clip, so the first URL seen for an id is held
  // until a different clip is opened. Signatures outlive any one viewing.
  const held = useRef<Clip | null>(null);
  if (!item?.videoUrl) {
    held.current = null;
  } else if (held.current?.id !== item.id) {
    held.current = {
      id: item.id,
      timestamp: new Date().toISOString(),
      thumbnailUrl: null,
      videoUrl: item.videoUrl,
      duration: 0,
    };
  }

  return {
    clip: held.current,
    handleDownload: item?.downloadUrl ? handleDownload : undefined,
  };
}
